import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { SocialProvider, SocialTarget } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useSocialTargets } from '../hooks/use-social-targets';
import type { PostTargetForm } from '../schemas/post-form-schema';

const NO_SECTION = '__none__';

export interface BoardPickerProps {
  storeId: string;
  provider: SocialProvider;
  value: PostTargetForm | null;
  onChange: (target: PostTargetForm | null) => void;
  disabled?: boolean;
  /** Ids for the labels, so the form can point its error at the board field. */
  boardId: string;
  sectionId: string;
}

/**
 * The board (and optional section) a pin goes to, read live from the network on every open of
 * the editor. No inline create in v1: a new board is made on Pinterest and shows up on the next
 * read.
 */
export function BoardPicker({
  storeId,
  provider,
  value,
  onChange,
  disabled = false,
  boardId,
  sectionId,
}: BoardPickerProps) {
  const { t } = useTranslation('social');
  const targets = useSocialTargets(storeId, provider, !disabled);
  const boards = targets.data?.items ?? [];
  const board = value ? boards.find((b) => b.id === value.boardId) : undefined;
  // A saved board the network no longer lists stays selectable, so the editor shows what it holds.
  const stale = value && !board ? value : null;

  const pickBoard = (id: string) => {
    const next = boards.find((b) => b.id === id);
    onChange(next ? { boardId: next.id, boardName: next.name, boardSectionId: null } : null);
  };
  const pickSection = (id: string) => {
    if (!value) return;
    onChange({ ...value, boardSectionId: id === NO_SECTION ? null : id });
  };

  if (targets.error && !disabled) {
    const notConnected =
      isApiError(targets.error) &&
      (targets.error.code === 'SOCIAL_NOT_CONNECTED' ||
        targets.error.code === 'SOCIAL_CONNECTION_EXPIRED');
    return notConnected ? (
      <Alert role="status">
        <AlertDescription>
          {t('editor.board.notConnected')}{' '}
          <Link
            to="/s/$storeId/social/pinterest/connection"
            params={{ storeId }}
            className="underline"
          >
            {t('editor.board.channelsLink')}
          </Link>
        </AlertDescription>
      </Alert>
    ) : (
      <ErrorState compact error={targets.error} onRetry={() => void targets.refetch()} />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor={boardId}>{t('editor.board.label')}</Label>
        {targets.isPending && !disabled ? (
          <Skeleton className="h-9" />
        ) : (
          <Select value={value?.boardId ?? ''} onValueChange={pickBoard} disabled={disabled}>
            <SelectTrigger id={boardId} className="w-full">
              <SelectValue placeholder={t('editor.board.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {stale ? <SelectItem value={stale.boardId}>{stale.boardName}</SelectItem> : null}
              {boards.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {boardLabel(b, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {!targets.isPending && boards.length === 0 && !disabled ? (
          <p className="text-muted-foreground text-xs">{t('editor.board.empty')}</p>
        ) : null}
      </div>
      {board && board.sections.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor={sectionId}>{t('editor.board.section')}</Label>
          <Select
            value={value?.boardSectionId ?? NO_SECTION}
            onValueChange={pickSection}
            disabled={disabled}
          >
            <SelectTrigger id={sectionId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_SECTION}>{t('editor.board.noSection')}</SelectItem>
              {board.sections.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </div>
  );
}

function boardLabel(board: SocialTarget, t: TFunction<'social'>): string {
  const privacy = board.privacy === 'public' ? null : t(`editor.board.privacy.${board.privacy}`);
  return privacy ? `${board.name} · ${privacy}` : board.name;
}
